/**
 * Wigglitz Free-Gift Modal
 * -------------------------
 * Email -> "Congrats! You get a free Winky." -> pick a character -> in the cart.
 *
 * The gift is an existing $0 product, so it checks out free on its own -- there
 * is no discount code and no discount row. Note that means the popup is NOT a
 * lock: a determined person can force-add a $0 Winky via /cart/add.js without
 * ever seeing this modal. That was weighed and accepted (2026-09-05) because
 * those products are already public and already $0; the popup advertises the
 * hole but does not create it. Do not mistake anything in this file for
 * entitlement enforcement -- it is all UX.
 *
 * Suppression state uses a COOKIE, not localStorage: custom.js calls
 * localStorage.clear() on every checkout click (see custom.js ~2708), which
 * would wipe a localStorage flag out from under us.
 */

(function () {
  'use strict';

  var cfg = window.wigglitzGiftConfig || {};
  var LIST_ID = cfg.klaviyoListId || '';
  var PROP = cfg.property || '_free_gift';
  var COOKIE = cfg.storageKey || 'wigglitz-gift-claimed';
  var SOURCE = cfg.source || 'Free Winky Popup';
  var AUTO_OPEN_MS = 8000;
  var MODE = cfg.mode === 'standalone' ? 'standalone' : 'klaviyo';
  var KLAVIYO_FORM_IDS = String(cfg.klaviyoFormId || '')
    .split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  // ?giftdebug=1 logs every Klaviyo form event to the console. Use it to read
  // the REAL event shape off a live submit instead of trusting documentation.
  var DEBUG = /[?&]giftdebug=1(&|$)/.test(window.location.search);
  // Let Klaviyo's own success message land before the picker takes over.
  var HANDOFF_DELAY_MS = 1200;

  // Endpoint that answers "has this email already RECEIVED a Winky?". Blank
  // disables the whole check and the picker opens for everyone -- which is the
  // shipped behaviour until the worker is deployed.
  var CHECK_URL = cfg.giftCheckUrl || '';
  // The lookup is fired on SUBMIT, while the shopper is still reading Klaviyo's
  // thank-you step, so its answer is already waiting when they dismiss it. This
  // is why the check adds no visible delay.
  var claimedCheck = null;
  // Must exceed the worker's own budget (2000ms for its single Klaviyo call) or
  // the client gives up first and fails open -- and the slow path is exactly
  // the one that can return claimed:true, so a tight budget here biases the
  // whole feature towards handing out duplicates.
  var CHECK_TIMEOUT_MS = 3000;
  // Remembering the address is what lets the reopen below finish the job on the
  // NEXT page. Short-lived and first-party: it exists only to bridge that gap.
  var EMAIL_COOKIE = COOKIE + '-email';

  var modal = null;

  // Unused on the $0 route (the gift is already free, so nothing needs
  // discounting). Kept wired because wigglitzGiftSetCode is the hook if the
  // priced-product + one-time-code design is ever revisited.
  var giftCode = '';

  /* ---------------------------------------------------------------- cookies */

  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie =
        name + '=' + encodeURIComponent(value) +
        ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    } catch (e) { /* cookies blocked; degrade to showing the popup again */ }
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? decodeURIComponent(m[2]) : '';
    } catch (e) { return ''; }
  }

  /* ------------------------------------------------------------- step logic */

  function showStep(which) {
    if (!modal) return;
    modal.querySelectorAll('[data-gift-step]').forEach(function (el) {
      el.hidden = el.getAttribute('data-gift-step') !== which;
    });
    // Move the dialog's accessible name onto the heading actually on screen.
    // aria-labelledby resolves even when the target is hidden, so leaving it on
    // step 1 made the picker announce itself as "Enter email for free gift".
    if (modal.querySelector('#gift-modal-title-' + which)) {
      modal.setAttribute('aria-labelledby', 'gift-modal-title-' + which);
    }
  }

  function openModal() {
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gift-modal-open');
    // Focus the first control in the step that is actually showing. This used to
    // always target the email input, which in klaviyo mode lives inside a hidden
    // step -- focusing it is a no-op, so focus never entered the dialog at all
    // and keyboard users were left behind an aria-modal element.
    setTimeout(function () {
      var open = modal.querySelector('[data-gift-step]:not([hidden])');
      var first = open && open.querySelector(
        'input:not([type="hidden"]), button:not([disabled]), [href], select, textarea');
      (first || modal.querySelector('.gift-modal__close') || modal).focus();
    }, 80);
  }

  /**
   * Open straight at the character picker. Used for the Klaviyo hand-off, where
   * the email has already been captured by Klaviyo's own form.
   */
  function isLiveTheme() {
    try {
      return !!(window.Shopify && window.Shopify.theme &&
                window.Shopify.theme.role === 'main');
    } catch (e) { return false; }
  }

  function forcedPreview() {
    // QA door -- deliberately DEAD on the published theme. ?gift=1 opens the
    // picker with NO email and ignores the cookie, so on live it is a shareable
    // free-gift link: one post to a deals site and it is open season. On any
    // unpublished/preview theme it still works, which is where demos and QA
    // actually happen. Shopify.theme.role === 'main' only on the live theme.
    if (isLiveTheme()) return false;
    return /[?&]gift=1(&|$)/.test(window.location.search);
  }

  function openAtKnown() {
    if (!modal || !modal.hidden) return;
    showStep('known');
    openModal();
  }

  /**
   * Pull the submitted address out of Klaviyo's event. The shape of metaData is
   * undocumented and has changed before, so try the known keys and then fall
   * back to any value that looks like an email rather than trusting one path.
   */
  function emailFromSubmit(d) {
    var m = (d && d.metaData) || {};
    var direct = m.$email || m.email || m.Email;
    if (typeof direct === 'string' && direct.indexOf('@') > 0) return direct;
    for (var k in m) {
      if (typeof m[k] === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(m[k])) return m[k];
    }
    return '';
  }

  /**
   * Ask the proxy whether this email already has its Winky.
   *
   * FAILS OPEN in every branch -- no URL, no email, bad status, bad JSON,
   * network error, or slower than CHECK_TIMEOUT_MS all resolve false. Handing
   * out an occasional duplicate costs one Winky; wrongly refusing a genuine new
   * subscriber costs the signup we just paid for.
   */
  function checkAlreadyClaimed(email) {
    if (!CHECK_URL || !email) return Promise.resolve(false);
    var settled = false;
    return new Promise(function (resolve) {
      var done = function (v) { if (!settled) { settled = true; resolve(v); } };
      setTimeout(function () { done(false); }, CHECK_TIMEOUT_MS);
      fetch(CHECK_URL, {
        method: 'POST',
        // Cross-origin to the worker. Send no cookies: the endpoint needs none,
        // and omitting them keeps the request simple and cacheless.
        credentials: 'omit',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (DEBUG) console.log('[gift] claim check:', JSON.stringify(data));
          done(!!(data && data.claimed));
        })
        .catch(function () { done(false); });
    });
  }

  function openAtPicker() {
    if (!modal || !modal.hidden) return;
    showStep('pick');
    openModal();
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gift-modal-open');
    // Dismissing without claiming still suppresses for a while, so the popup
    // does not re-nag on every page view.
    //
    // 'subscribed' -> 'retried' is the important one. Before this, closing while
    // subscribed wrote NOTHING, and the reopen below was guarded only by
    // sessionStorage (per TAB) against a 365-day cookie -- so every new tab
    // re-opened the picker, for a year. Closing now burns the one reopen.
    // Only a never-subscribed visitor is suppressed here. A 'subscribed' shopper
    // keeps that state on purpose: they were PROMISED a Winky, and one stray tap
    // on the backdrop must not cost them it. The single reopen below is what
    // burns the state, and only once it has actually rendered.
    if (!getCookie(COOKIE)) setCookie(COOKIE, 'dismissed', 7);
  }

  /* ----------------------------------------------------------------- step 1 */

  function handleEmailSubmit(e) {
    e.preventDefault();

    var form = e.currentTarget;
    var input = form.querySelector('.js-gift-email-input');
    var button = form.querySelector('.js-gift-email-submit');
    var error = form.querySelector('.js-gift-email-error');
    var email = input && input.value ? input.value.trim() : '';

    if (!email || email.indexOf('@') === -1) {
      if (input) input.focus();
      return;
    }

    // Already claimed on this device -- say so rather than handing out a second.
    //
    // NOTE: this catches the repeat visitor, not every existing subscriber.
    // Klaviyo's AJAX subscribe endpoint answers "ok" for a profile it already
    // has, so the browser genuinely cannot tell a new signup from an old one.
    // Real detection arrives with the codes: Klaviyo mints no code for an
    // existing profile, and showKnown() is called when none comes back.
    if (getCookie(COOKIE) === 'claimed' || getCookie(COOKIE) === 'subscribed') {
      showKnown();
      return;
    }

    if (error) error.hidden = true;
    var original = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'One moment…';
    }

    var restore = function () {
      if (button) {
        button.disabled = false;
        button.textContent = original;
      }
    };

    // No list configured: don't strand the shopper, just move on. The gift is
    // still gated by the code, so this is safe.
    if (!LIST_ID) {
      showStep('pick');
      restore();
      return;
    }

    fetch('https://a.klaviyo.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ g: LIST_ID, email: email, $source: SOURCE }).toString()
    })
      .then(function (res) {
        if (!res.ok) throw new Error('subscribe failed');
        setCookie(COOKIE, 'subscribed', 365);
        showStep('pick');
        restore();
      })
      .catch(function () {
        if (error) error.hidden = false;
        restore();
      });
  }

  /**
   * "It looks like you've already subscribed." Shown in place of the picker, so
   * there is no path from this state to a second gift.
   */
  function showKnown() {
    if (!modal) return;
    var note = modal.querySelector('.js-gift-known');
    var err = modal.querySelector('.js-gift-email-error');
    var form = modal.querySelector('.js-gift-email-form');
    if (err) err.hidden = true;
    if (note) note.hidden = false;
    if (form) {
      var b = form.querySelector('.js-gift-email-submit');
      if (b) { b.disabled = true; b.textContent = 'Already subscribed'; }
    }
  }

  /* --------------------------------------------------------------- discount */

  /**
   * Put the subscriber's one-time code on the cart session.
   *
   * This is what makes the cart honestly say "Free": Shopify applies the code
   * to the cart, so the line's final_line_price really does become 0 and the
   * subtotal is correct before checkout. Without it the shopper would see the
   * gift's real price until the checkout page, which is exactly the "looks like
   * a discount" experience we are avoiding.
   *
   * Fetching /discount/<code> sets the cookie without navigating away.
   */
  function applyDiscount(code) {
    if (!code) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(code), { credentials: 'include' })
      .catch(function () { /* checkout still honours the code; cart just shows the price */ });
  }

  /* ----------------------------------------------------------------- step 2 */

  function alreadyHasGift(cart) {
    return (cart.items || []).some(function (item) {
      return item.properties && item.properties[PROP];
    });
  }

  var picking = false;

  function handlePick(e) {
    var button = e.target.closest('.js-gift-pick');
    if (!button || button.disabled) return;
    // Module-level latch. Disabling the clicked button is not enough: the add is
    // async, and a fast second tap on a DIFFERENT tile lands before the first
    // resolves -- two $0 lines, both carrying _free_gift, so the cart sweep
    // protects both. One latch covers every tile.
    if (picking) return;
    picking = true;

    var variantId = button.getAttribute('data-variant-id');
    var character = button.getAttribute('data-character') || 'Winky';
    // Reuse the tile's own image on the confirmation step. The empty-state
    // thumb is a <span>, not an <img>, so check the tag before reading src.
    var pickedThumb = button.querySelector('.gift-modal__thumb');
    var pickedImg = pickedThumb && pickedThumb.tagName === 'IMG' ? pickedThumb.src : '';
    var error = modal.querySelector('.js-gift-pick-error');
    if (!variantId) return;

    if (error) error.hidden = true;
    button.classList.add('is-loading');
    button.disabled = true;

    var fail = function () {
      picking = false;
      if (error) error.hidden = false;
      button.classList.remove('is-loading');
      button.disabled = false;
    };

    // One gift per cart. Checked here for a clean message; the real ceiling is
    // the single-use code at checkout.
    fetch('/cart.js', { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (alreadyHasGift(cart)) {
          // They are done -- record it. Without this the cookie stayed at
          // 'subscribed'/'retried', so the picker kept re-offering a gift the
          // cart already held. Confirm the Winky in the cart, not the tile they
          // just pressed, or the screen would name the wrong character.
          setCookie(COOKIE, 'claimed', 365);
          var held = (cart.items || []).filter(function (i) {
            return i.properties && i.properties[PROP];
          })[0];
          var heldName = (held && held.properties[PROP]) || character;
          var heldImg = (held && held.image) || pickedImg;
          finish(heldName, heldImg);
          return null;
        }
        var props = {};
        props[PROP] = character;
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1, properties: props })
        }).then(function (res) {
          if (!res.ok) throw new Error('add failed');
          setCookie(COOKIE, 'claimed', 365);
          return applyDiscount(giftCode).then(function () { finish(character, pickedImg); });
        });
      })
      .catch(fail);
  }

  /**
   * The cart page is server-rendered, so a gift added by fetch() is invisible
   * there: the line list, the subtotal and the header count all still show the
   * old cart while the modal says "added to your cart". Shoppers read that as a
   * failure and pick again. Reload once so the page tells the truth.
   */
  function refreshIfOnCart() {
    if (!/^\/cart(\/|$)/.test(window.location.pathname)) return;
    setTimeout(function () { window.location.reload(); }, 1400);
  }

  function finish(character, imgSrc) {
    refreshIfOnCart();
    var nameEl = modal.querySelector('.js-gift-chosen-name');
    if (nameEl) nameEl.textContent = character;

    var imgEl = modal.querySelector('.js-gift-chosen-img');
    if (imgEl) {
      if (imgSrc) {
        imgEl.src = imgSrc;
        imgEl.alt = character;
        imgEl.hidden = false;
      } else {
        imgEl.hidden = true;
      }
    }
    showStep('done');
    document.dispatchEvent(new CustomEvent('wigglitz:gift-added', { detail: { character: character } }));
  }

  /* --------------------------------------------------- gift-only cart guard */

  /**
   * "Free with your next purchase" is copy, not a rule. A cart holding nothing
   * but the $0 Winky has a $0 subtotal and a working Check out button, so the
   * gift ships on its own for the price of postage. This blocks that on the cart
   * page. It is a guard rail, not a lock -- the real ceiling would be a Shopify
   * cart-validation Function, which is a separate piece of work.
   */
  function guardGiftOnlyCart() {
    if (!/^\/cart(\/|$)/.test(window.location.pathname)) return;

    fetch('/cart.js', { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        if (!items.length) return;
        var isGift = function (i) { return !!(i.properties && i.properties[PROP]); };
        // Only act when EVERY line is a gift. A cart with a gift plus real
        // product is exactly what we want and must not be touched.
        if (!items.every(isGift)) return;

        var btn = document.querySelector('button[name="checkout"], .cart-submit');
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        }
        // Express buttons (Shop Pay etc.) bypass the form entirely, so hide them.
        var extra = document.querySelector('.additional-checkout-buttons');
        if (extra) extra.style.display = 'none';

        if (!document.querySelector('.gift-only-note')) {
          var note = document.createElement('p');
          note.className = 'gift-only-note';
          note.setAttribute('role', 'status');
          note.textContent = 'Add something to your order to claim your free Winky \u2014 it comes free with a purchase.';
          if (btn && btn.parentNode) btn.parentNode.insertBefore(note, btn);
        }
      })
      .catch(function () { /* never block a real cart on a failed probe */ });
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    modal = document.getElementById('gift-modal');
    if (!modal) return;

    var emailForm = modal.querySelector('.js-gift-email-form');
    if (emailForm) emailForm.addEventListener('submit', handleEmailSubmit);

    var grid = modal.querySelector('.js-gift-grid');
    if (grid) grid.addEventListener('click', handlePick);

    modal.querySelectorAll('[data-gift-dismiss]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && !modal.hidden) closeModal();
    });

    // ?gift=1 forces the modal open immediately, ignoring both the 8s delay and
    // the already-seen cookie. For previewing and QA -- harmless in the wild,
    // since the modal hands out nothing a plain visit could not also get.
    // ---- Klaviyo hand-off -------------------------------------------------
    // Klaviyo fires `klaviyoForms` on its own forms. We take the picker over on
    // a successful submit of OUR form only -- this store puts many Klaviyo forms
    // on a page, and a footer newsletter signup must not hand out a Winky.
    if (MODE === 'klaviyo') {
      window.addEventListener('klaviyoForms', function (ev) {


        var d = ev.detail || {};
        if (DEBUG) console.log('[gift] klaviyoForms event:', JSON.stringify(d));

        // Unknown form id => do nothing. This fails CLOSED on purpose: if
        // Klaviyo ever renames the field or the form is cloned, the worst case
        // is nobody is offered a Winky, never that everybody is.
        if (KLAVIYO_FORM_IDS.length && KLAVIYO_FORM_IDS.indexOf(d.formId) === -1) {
          if (DEBUG) console.log('[gift] ignoring form', d.formId, '- watching', KLAVIYO_FORM_IDS);
          return;
        }

        // TWO-PART HAND-OFF (changed 2026-09-06, Matthew's call).
        //
        // Submitting only RECORDS the subscription. It does NOT open the picker,
        // because Klaviyo shows its own "Thanks for subscribing!" success step
        // straight after submit -- opening on top of that stacked two popups and
        // forced the shopper to close both. Instead we wait for Klaviyo's own
        // close, so exactly one popup is ever on screen.
        if (d.type === 'submit') {
          setCookie(COOKIE, 'subscribed', 365);
          // Start the lookup NOW, not on close. The shopper spends a beat on
          // Klaviyo's thank-you step, and that beat is enough to cover the
          // round trip, so the decision is ready the moment they dismiss it.
          var submittedEmail = emailFromSubmit(d);
          if (submittedEmail) setCookie(EMAIL_COOKIE, encodeURIComponent(submittedEmail), 365);
          claimedCheck = checkAlreadyClaimed(submittedEmail);
          if (DEBUG) console.log('[gift] subscribed - holding picker until Klaviyo closes');
          return;
        }

        // ANY dismissal of the success step hands over to the picker -- the
        // "Select Your Gift" button, the X, Escape, or the backdrop.
        //
        // Matthew's call, 2026-09-07, after watching a real shopper test it:
        // once someone has actually subscribed they have EARNED the Winky, so
        // how they happen to dismiss the thank-you step must not decide whether
        // they get to choose it. An X there means "close this box", not
        // "forfeit my gift".
        //
        // The step-1 X is still ignored, and the COOKIE is what draws that
        // line -- it is only written on 'submit', so a close from someone who
        // never subscribed finds no cookie and returns here. That is why this
        // needs no inspection of which button was clicked.
        //
        // (An earlier build did distinguish the X from the button via the DOM.
        // It worked -- see _snapshots/2026-09-07_pre-either-way -- but the
        // product decision above made it unnecessary, so it was removed rather
        // than left in as dead weight.)
        if (d.type === 'close' || d.type === 'redirectedToUrl') {
          // 'retried' counts as well as 'subscribed'. The reopen timer is
          // per-tab but the cookie is shared, so a second wigglitz.com tab
          // loading in the seconds between submit and dismiss would flip this
          // to 'retried' and this guard would then swallow the picker in the
          // tab the shopper is actually looking at -- popup fades, nothing
          // follows, no recovery. Only 'claimed' and unset should stop us.
          var st = getCookie(COOKIE);
          if (st !== 'subscribed' && st !== 'retried') return;
          // Someone who already RECEIVED a Winky gets the thank-you panel
          // instead of the picker. With no CHECK_URL configured this resolves
          // false immediately and the behaviour is identical to before.
          (claimedCheck || Promise.resolve(false)).then(function (claimed) {
            claimedCheck = null;
            if (claimed) {
              // They have theirs. Record it so the retry on the next page load
              // does not re-offer a gift we just declined.
              setCookie(COOKIE, 'claimed', 365);
              if (DEBUG) console.log('[gift] already claimed - showing thank-you panel');
              setTimeout(openAtKnown, 350);
            } else {
              if (DEBUG) console.log('[gift] dismissed after subscribing - opening picker');
              // Short beat so Klaviyo's own fade-out finishes first.
              setTimeout(openAtPicker, 350);
            }
          });
        }

      });
    }

    var forced = forcedPreview();

    // Never auto-open on the way to paying. Someone on the cart or checkout page
    // has already decided to buy; interrupting them there is pure friction and
    // costs more than the gift is worth. The modal can still be opened by hand
    // (wigglitzGiftOpen) if a link or banner wants to offer it.
    var onCheckoutPath = /^\/(cart|checkout)(\/|$)/.test(window.location.pathname);

    // Subscribed but never chose -- reopen the picker once, on the next page
    // they land on. Without this, one stray click costs them the gift they were
    // just promised, and costs us the goodwill the signup was meant to buy.
    if (MODE === 'klaviyo' && !forcedPreview() &&
        getCookie(COOKIE) === 'subscribed' && !onCheckoutPath) {
      // Burn the reopen only once it has ACTUALLY rendered. Writing the cookie
      // up front lost the gift for anyone who bounced or backgrounded the tab
      // inside the 2.5s window. sessionStorage alone was not enough either --
      // it is per-tab, so it could not restrain a 365-day cookie across tabs.
      try { sessionStorage.setItem('giftRetried', '1'); } catch (e) {}
      setTimeout(function () {
        // Re-run the claim check here. Without it this reopen is a hole straight
        // through the whole feature: the answer from the previous page lives
        // only in a promise that died with that page, so an already-claimed
        // shopper who navigated before it landed would be handed the picker --
        // permanently, and on every device.
        var saved = getCookie(EMAIL_COOKIE);
        var email = saved ? decodeURIComponent(saved) : '';
        // Spend the reopen BEFORE asking, not after. Writing it in the callback
        // meant a slow or failed check left the cookie on 'subscribed', so the
        // next page load armed the whole thing again -- one shopper firing the
        // lookup on every pageview, which is also how a mobile carrier NAT
        // shares its way through the rate limit.
        setCookie(COOKIE, 'retried', 365);
        checkAlreadyClaimed(email).then(function (claimed) {
          if (claimed) setCookie(COOKIE, 'claimed', 365);
          if (claimed) {
            if (DEBUG) console.log('[gift] reopen: already claimed - thank-you panel');
            openAtKnown();
          } else {
            openAtPicker();
          }
        });
      }, 2500);
    }

    if (forced) {
      // Preview the step this mode actually owns.
      showStep(MODE === 'klaviyo' ? 'pick' : 'email');
      setTimeout(openModal, 60);
    } else if (MODE === 'standalone' && !getCookie(COOKIE) && !onCheckoutPath) {
      // Only standalone mode opens itself on a timer. In klaviyo mode Klaviyo
      // decides when to show the popup -- a second self-opening modal is how you
      // get two popups fighting over the screen.
      setTimeout(function () {
        if (modal.hidden) openModal();
      }, AUTO_OPEN_MS);
    }

    // Runs on the cart page only; no-ops everywhere else.
    guardGiftOnlyCart();

    window.wigglitzGiftOpen = openModal;
    window.wigglitzGiftOpenPicker = openAtPicker;
    window.wigglitzGiftSetCode = function (code) { giftCode = code || ''; };
    window.wigglitzGiftShowKnown = showKnown;
    window.wigglitzGiftClose = closeModal;
    window.wigglitzGiftReset = function () {
      setCookie(COOKIE, '', -1);
      showStep('email');
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
